<template>
  <div class="construction-banner" role="status" aria-live="polite">
    This website is under construction. Expect changes.
  </div>
  <div id="app">
    <header class="site-header">
      <router-link to="/" class="logo-link" aria-label="Return to recipes">
        <img src="/logo.webp" alt="Grandma Swapp's Cookbook" class="brand-logo">
      </router-link>
      <div class="header-content">
        <div class="brand-row">
          <router-link to="/" class="brand-title" aria-label="Return to recipes">
            Grandma Swapp's Cookbook
          </router-link>
        </div>
        <nav class="site-nav">
          <router-link to="/" class="nav-link">Cookbook</router-link>
          <router-link to="/about" class="nav-link">About Grandma Swapp</router-link>
          <div class="theme-bar">
            <span class="theme-label">Theme</span>
            <select v-model="theme" class="theme-select" aria-label="Theme selector">
              <option v-for="option in themeOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </nav>
      </div>
    </header>
    <div class="categories-section">
      <div class="categories-container">
        <button
          class="category-chip"
          :class="{ active: selectedCategory === 'All' }"
          type="button"
          @click="onCategorySelect('All')"
        >
          <span class="category-name">All Recipes</span>
          <span class="category-count">{{ totalRecipeCount }}</span>
        </button>
        <button
          v-for="category in categoriesWithCounts"
          :key="category.name"
          class="category-chip"
          :class="{ active: selectedCategory === category.name }"
          type="button"
          @click="onCategorySelect(category.name)"
        >
          <span class="category-name">{{ category.name }}</span>
          <span class="category-count">{{ category.count }}</span>
        </button>
      </div>
    </div>
    <router-view />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const themeOptions = [
  { value: 'dark', label: 'Midnight' },
  { value: 'light', label: 'Parchment' },
  { value: 'harvest', label: 'Harvest' }
]

const theme = ref('dark')
const categories = ref([])
const recipeCounts = ref({})
const totalRecipeCount = ref(0)
const selectedCategory = ref('All')
const route = useRoute()
const router = useRouter()
const apiUrl = process.env.VUE_APP_API_URL

const categoriesWithCounts = computed(() => {
  return categories.value.map(cat => ({
    name: cat,
    count: recipeCounts.value[cat] || 0
  }))
})

const applyTheme = (value) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', value)
  }
}

const openSettingsDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    resolve(null)
    return
  }

  const request = indexedDB.open('cookbook', 1)

  request.onupgradeneeded = (event) => {
    const db = event.target.result
    if (!db.objectStoreNames.contains('settings')) {
      db.createObjectStore('settings')
    }
  }

  request.onsuccess = () => resolve(request.result)
  request.onerror = () => reject(request.error)
})

const getStoredTheme = async () => {
  try {
    const db = await openSettingsDb()
    if (!db) return null

    return await new Promise((resolve) => {
      const transaction = db.transaction('settings', 'readonly')
      const store = transaction.objectStore('settings')
      const getRequest = store.get('theme')
      getRequest.onsuccess = () => resolve(getRequest.result || null)
      getRequest.onerror = () => resolve(null)
      transaction.oncomplete = () => db.close()
    })
  } catch (error) {
    return null
  }
}

const setStoredTheme = async (value) => {
  try {
    const db = await openSettingsDb()
    if (!db) return

    await new Promise((resolve) => {
      const transaction = db.transaction('settings', 'readwrite')
      const store = transaction.objectStore('settings')
      store.put(value, 'theme')
      transaction.oncomplete = () => {
        db.close()
        resolve()
      }
      transaction.onerror = () => resolve()
    })
  } catch (error) {
    return
  }
}

const loadCategories = async () => {
  try {
    let data = []
    if (apiUrl) {
      const response = await fetch(`${apiUrl}/recipes`)
      if (!response.ok) throw new Error('Failed to fetch recipes from API')
      data = await response.json()
    } else {
      const response = await fetch('/recipes/index.json')
      data = await response.json()
    }
    
    // Calculate total
    totalRecipeCount.value = data.length
    
    // Count recipes per category
    const counts = {}
    data.forEach((recipe) => {
      if (recipe.category) {
        counts[recipe.category] = (counts[recipe.category] || 0) + 1
      }
    })
    recipeCounts.value = counts
    
    const categorySet = new Set(data.map((recipe) => recipe.category).filter(Boolean))
    categories.value = [...categorySet].sort()
  } catch (error) {
    console.error('Failed to load categories:', error)
  }
}

const syncCategoryFromRoute = () => {
  const category = route.query.category
  if (!category || typeof category !== 'string' || !category.length) {
    selectedCategory.value = 'All'
    return
  }
  
  // Validate that the category exists in our known categories list
  if (category === 'All' || categories.value.includes(category)) {
    selectedCategory.value = category
  } else {
    // Invalid category - redirect to All
    selectedCategory.value = 'All'
    router.replace({ path: '/', query: {} })
  }
}

const onCategoryChange = () => {
  const category = selectedCategory.value
  if (category === 'All') {
    router.push({ path: '/', query: {} })
    return
  }
  router.push({ path: '/', query: { category } })
}

const onCategorySelect = (category) => {
  selectedCategory.value = category
  onCategoryChange()
}

onMounted(() => {
  getStoredTheme().then((storedTheme) => {
    theme.value = storedTheme || 'light'
    applyTheme(theme.value)
  })
  loadCategories()
  syncCategoryFromRoute()
})

watch(() => route.query.category, () => {
  syncCategoryFromRoute()
})

watch(theme, (value) => {
  applyTheme(value)
  setStoredTheme(value)
})
</script>

<style>
[data-theme='dark'] {
  color-scheme: dark;
  --page-bg: radial-gradient(circle at top, #1d1f25 0%, #111214 50%, #0b0c0f 100%);
  --surface: #1a1d22;
  --surface-alt: #21262d;
  --text: #f4efe6;
  --muted-text: #c2b9ab;
  --border: #2e343c;
  --accent: #f1a66a;
  --accent-strong: #ffbf86;
  --accent-contrast: #1b1410;
  --input-bg: #121418;
  --input-border: #2f353d;
  --header-bg: linear-gradient(135deg, #1c1f25 0%, #2a2f37 100%);
  --shadow: 0 16px 30px rgba(0, 0, 0, 0.45);
  --chip-bg: #171a1f;
  --chip-border: #4a5665;
  --banner-bg: #b14a1f;
  --banner-text: #fff5ec;
}

[data-theme='light'] {
  color-scheme: light;
  --page-bg: radial-gradient(circle at top, #fdf7ee 0%, #f4ecdf 60%, #e7dccb 100%);
  --surface: #ffffff;
  --surface-alt: #f8f2e7;
  --text: #2b2219;
  --muted-text: #675a4d;
  --border: #e2d6c4;
  --accent: #b36a2c;
  --accent-strong: #d88433;
  --accent-contrast: #ffffff;
  --input-bg: #fffaf4;
  --input-border: #d8cbb8;
  --header-bg: linear-gradient(135deg, #f2dfc6 0%, #e8c59f 100%);
  --shadow: 0 14px 24px rgba(82, 52, 27, 0.15);
  --chip-bg: #fff5e8;
  --chip-border: color-mix(in srgb, var(--border) 70%, #000 30%);
  --banner-bg: #8c2f1a;
  --banner-text: #fff6f3;
}

[data-theme='harvest'] {
  color-scheme: dark;
  --page-bg: radial-gradient(circle at top, #2a241a 0%, #1d1912 65%, #15110c 100%);
  --surface: #261f16;
  --surface-alt: #33281d;
  --text: #f3e8d6;
  --muted-text: #c8b69e;
  --border: #403327;
  --accent: #d88b4b;
  --accent-strong: #f1a45a;
  --accent-contrast: #1f140a;
  --input-bg: #1c160f;
  --input-border: #4a3a2b;
  --header-bg: linear-gradient(135deg, #5a3a1f 0%, #7a4a25 100%);
  --shadow: 0 16px 26px rgba(0, 0, 0, 0.45);
  --chip-bg: #2a2118;
  --chip-border: #6a5744;
  --banner-bg: #c0521f;
  --banner-text: #fff4e8;
}

* {
  box-sizing: border-box;
}

html,
body {
  min-height: 100%;
  margin: 0;
  padding: 0;
  background: var(--page-bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 18px;
  line-height: 1.6;
}

#app {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.construction-banner {
  width: 100%;
  background: var(--banner-bg);
  color: var(--banner-text);
  text-align: center;
  padding: 10px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.site-header {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 20px 24px;
  margin-bottom: 28px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: start;
}

.logo-link {
  display: inline-flex;
  align-items: center;
}

.brand-logo {
  width: min(160px, 40vw);
  height: auto;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.brand-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.brand-title {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 2.6rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  text-decoration: none;
  color: var(--text);
}

.site-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.nav-link {
  color: var(--text);
  text-decoration: none;
  font-weight: 600;
  letter-spacing: 0;
  font-size: 1.2rem;
}

.nav-link.router-link-active {
  color: var(--accent);
}

.categories-section {
  margin-bottom: 28px;
}

.categories-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--chip-bg);
  border: 2px solid var(--chip-border);
  color: var(--text);
  padding: 10px 16px;
  border-radius: 999px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-chip:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.category-chip.active {
  background: var(--accent);
  color: var(--accent-contrast);
  border-color: var(--accent);
}

.category-name {
  font-weight: 600;
}

.category-count {
  background: var(--surface-alt);
  color: var(--muted-text);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
}

.category-chip.active .category-count {
  background: var(--accent-contrast);
  color: var(--accent);
}

.theme-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: var(--muted-text);
}

.theme-label {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
}

.theme-select {
  background: var(--surface-alt);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

@media (max-width: 720px) {
  #app {
    padding: 16px;
  }

  .theme-bar {
    justify-content: space-between;
  }

  .brand-title {
    font-size: 2rem;
  }

  .site-nav {
    justify-content: space-between;
  }

  .nav-dropdown {
    position: static;
    width: 100%;
  }

  .site-header {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .logo-link {
    justify-content: center;
  }
}
</style>
